import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { toDateNumber, toDisplayDate, toStorageDate } from "../utils/date";
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const timeToSeconds = (str) => {
  if (!str) return null;
  const parts = str.split(":");
  if (parts.length !== 2) return null;
  const [m, s] = parts.map(Number);
  if (Number.isNaN(m) || Number.isNaN(s) || m < 0 || s < 0 || s > 59) return null;
  return m * 60 + s;
};

const secondsToMMSS = (sec) => {
  if (sec == null) return "";
  const total = Number(sec);
  if (Number.isNaN(total)) return "";
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatDate = (value) => toDisplayDate(value);

const AdminUserProgressPage = () => {
  const { userId, tip } = useParams();
  const navigate = useNavigate();
  const tipTraseu = (tip || "").toUpperCase();
  const tipLower = tipTraseu.toLowerCase();

  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [rezEntries, setRezEntries] = useState([]);
  const [traseuEntries, setTraseuEntries] = useState([]);
  const [form, setForm] = useState({
    data: "",
    timpTraseu: "",
    timpRezistenta: "",
  });

  const identifier = decodeURIComponent(userId || "");

  useEffect(() => {
    const root = document.documentElement;
    const prevTheme = root.getAttribute("data-theme");
    root.setAttribute("data-theme", "light");
    return () => {
      if (prevTheme) {
        root.setAttribute("data-theme", prevTheme);
      } else {
        root.removeAttribute("data-theme");
      }
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (!identifier) return;
      let d = null;

      // incercam dupa id-ul documentului
      const snap = await getDoc(doc(db, "inscrieri_traseu", identifier));
      if (snap.exists()) {
        d = snap.data();
      } else if (identifier.includes("@")) {
        // fallback: cautam dupa email
        const qy = query(
          collection(db, "inscrieri_traseu"),
          where("email", "==", identifier)
        );
        const res = await getDocs(qy);
        if (!res.empty) d = res.docs[0].data();
      }

      if (d) {
        setUserName(d.numePrenume || d.nume || "Participant");
        setUserEmail(d.email || identifier);
      } else {
        const userSnap = await getDocs(
          query(
            collection(db, "users"),
            identifier.includes("@")
              ? where("email", "==", identifier)
              : where("uid", "==", identifier)
          )
        );
        const userDoc = userSnap.docs[0]?.data();
        if (userDoc) {
          setUserName(userDoc.displayName || userDoc.name || "Participant");
          setUserEmail(userDoc.email || identifier);
        } else {
          setUserName("Participant");
          if (identifier.includes("@")) setUserEmail(identifier);
        }
      }
    };
    fetchUser();
  }, [identifier]);

  useEffect(() => {
    if (!identifier || !tipLower) return;
    const load = async () => {
      const refRez = collection(db, `rezistentaData_${tipLower}`);
      const refTraseu = collection(db, `traseuData_${tipLower}`);

      const fetchByRef = async (ref) => {
        let snap = await getDocs(query(ref, where("userId", "==", identifier)));
        if (snap.empty && identifier.includes("@")) {
          snap = await getDocs(query(ref, where("userEmail", "==", identifier)));
        }
        return snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aNum = toDateNumber(a.data || a.createdAt || 0) || 0;
            const bNum = toDateNumber(b.data || b.createdAt || 0) || 0;
            return aNum - bNum;
          });
      };

      setRezEntries(await fetchByRef(refRez));
      setTraseuEntries(await fetchByRef(refTraseu));
    };
    load();
  }, [identifier, tipLower]);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const resetForm = () => {
    setForm({ data: "", timpTraseu: "", timpRezistenta: "" });
  };

  const saveEntry = async () => {
    const timpTraseuSec = timeToSeconds(form.timpTraseu);
    const timpRezSec = timeToSeconds(form.timpRezistenta);
    if (form.timpTraseu && timpTraseuSec === null)
      return alert("Timp traseu trebuie sa fie in format mm:ss (ex: 03:20).");
    if (form.timpRezistenta && timpRezSec === null)
      return alert("Timp rezistenta trebuie sa fie in format mm:ss (ex: 01:45).");

    const dataValue = toStorageDate(form.data || new Date());
    if (!dataValue) {
      return alert("Data trebuie sa fie in format dd/mm/yyyy.");
    }
    const basePayload = {
      userId: identifier,
      userEmail,
      userName,
      tipTraseu,
      data: dataValue,
      createdAt: new Date().toISOString(),
    };

    if (timpTraseuSec !== null) {
      await addDoc(collection(db, `traseuData_${tipLower}`), {
        ...basePayload,
        seconds: timpTraseuSec,
      });
    }
    if (timpRezSec !== null) {
      await addDoc(collection(db, `rezistentaData_${tipLower}`), {
        ...basePayload,
        seconds: timpRezSec,
      });
    }

    resetForm();
  };

  const removeRez = async (id) => {
    await deleteDoc(doc(db, `rezistentaData_${tipLower}`, id));
  };

  const removeTraseu = async (id) => {
    await deleteDoc(doc(db, `traseuData_${tipLower}`, id));
  };

  const rezChartData = useMemo(
    () =>
      rezEntries.map((e) => ({
        date: formatDate(e.data),
        seconds: e.seconds ?? null,
      })),
    [rezEntries]
  );

  const traseuChartData = useMemo(
    () =>
      traseuEntries.map((e) => ({
        date: formatDate(e.data),
        seconds: e.seconds ?? null,
      })),
    [traseuEntries]
  );

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "var(--color-bg)", color: "var(--color-text)" }}
    >
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-orange-500">
              Progres {tipTraseu}
            </p>
            <h1 className="text-3xl font-bold">
              Urmarire progres - {userName} ({tipTraseu})
            </h1>
            <p
              className="text-sm"
              style={{ color: "var(--color-text-secondary, #6b7280)" }}
            >
              Vizualizeaza si adauga timpi pentru participantul selectat.
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg font-semibold border"
            style={{
              borderColor: "var(--color-border, #d1d5db)",
              color: "var(--color-text)",
            }}
          >
            Inapoi
          </button>
        </div>

        <div
          className="rounded-xl p-5 space-y-4"
          style={{
            backgroundColor: "var(--color-bg)",
            border: "1px solid var(--color-border, #d1d5db)",
          }}
        >
          <div className="grid md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="zz/ll/yyyy"
              value={form.data}
              onChange={(e) => handleChange("data", e.target.value)}
              className="p-3 rounded-lg border"
              style={{
                borderColor: "var(--color-border, #d1d5db)",
                color: "#000",
              }}
            />
            <input
              type="text"
              placeholder="Timp traseu (mm:ss)"
              value={form.timpTraseu}
              onChange={(e) => handleChange("timpTraseu", e.target.value)}
              className="p-3 rounded-lg border"
              style={{
                borderColor: "var(--color-border, #d1d5db)",
                color: "#000",
              }}
            />
            <input
              type="text"
              placeholder="Timp rezistenta (mm:ss)"
              value={form.timpRezistenta}
              onChange={(e) => handleChange("timpRezistenta", e.target.value)}
              className="p-3 rounded-lg border"
              style={{
                borderColor: "var(--color-border, #d1d5db)",
                color: "#000",
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={saveEntry}
              className="px-5 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
            >
              Adauga
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div
            className="rounded-xl p-5 space-y-3"
            style={{
              backgroundColor: "var(--color-bg)",
              border: "1px solid var(--color-border, #d1d5db)",
            }}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Rezistenta</h2>
              <span style={{ color: "var(--color-text-secondary, #6b7280)" }}>
                {rezEntries.length} inregistrari
              </span>
            </div>

            {rezEntries.length === 0 && (
              <p style={{ color: "var(--color-text-secondary, #6b7280)" }}>
                Nu exista inregistrari pentru acest participant.
              </p>
            )}

            {rezEntries.length > 0 && (
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                {rezEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="p-3 rounded-lg flex items-center justify-between gap-2"
                    style={{ border: "1px solid var(--color-border, #d1d5db)" }}
                  >
                    <div>
                      <p className="font-semibold">{formatDate(entry.data)}</p>
                      <p
                        className="text-sm"
                        style={{
                          color: "var(--color-text-secondary, #6b7280)",
                        }}
                      >
                        {tipTraseu}
                      </p>
                    </div>
                    <div className="flex gap-3 text-sm items-center">
                      <span>{secondsToMMSS(entry.seconds)}</span>
                    </div>
                    <button
                      onClick={() => removeRez(entry.id)}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg"
                    >
                      Sterge
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className="rounded-xl p-5 space-y-3"
            style={{
              backgroundColor: "var(--color-bg)",
              border: "1px solid var(--color-border, #d1d5db)",
            }}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Traseu</h2>
              <span style={{ color: "var(--color-text-secondary, #6b7280)" }}>
                {traseuEntries.length} inregistrari
              </span>
            </div>

            {traseuEntries.length === 0 && (
              <p style={{ color: "var(--color-text-secondary, #6b7280)" }}>
                Nu exista inregistrari pentru acest participant.
              </p>
            )}

            {traseuEntries.length > 0 && (
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
                {traseuEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="p-3 rounded-lg flex items-center justify-between gap-2"
                    style={{ border: "1px solid var(--color-border, #d1d5db)" }}
                  >
                    <div>
                      <p className="font-semibold">{formatDate(entry.data)}</p>
                      <p
                        className="text-sm"
                        style={{
                          color: "var(--color-text-secondary, #6b7280)",
                        }}
                      >
                        {tipTraseu}
                      </p>
                    </div>
                    <div className="flex gap-3 text-sm items-center">
                      <span>{secondsToMMSS(entry.seconds)}</span>
                    </div>
                    <button
                      onClick={() => removeTraseu(entry.id)}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg"
                    >
                      Sterge
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className="rounded-xl p-5 space-y-4"
          style={{
            backgroundColor: "var(--color-bg)",
            border: "1px solid var(--color-border, #d1d5db)",
          }}
        >
          <h2 className="text-xl font-semibold">Grafice progres</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={traseuChartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border, #d1d5db)"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "var(--color-text-secondary, #6b7280)",
                      fontSize: 12,
                    }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={50}
                  />
                  <YAxis
                    tickFormatter={secondsToMMSS}
                    tick={{
                      fill: "var(--color-text-secondary, #6b7280)",
                      fontSize: 12,
                    }}
                  />
                  <Tooltip formatter={(v) => secondsToMMSS(v)} />
                  <Area
                    type="monotone"
                    dataKey="seconds"
                    stroke="#f97316"
                    fill="#f97316"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    name="Timp traseu"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={rezChartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border, #d1d5db)"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "var(--color-text-secondary, #6b7280)",
                      fontSize: 12,
                    }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={50}
                  />
                  <YAxis
                    tickFormatter={secondsToMMSS}
                    tick={{
                      fill: "var(--color-text-secondary, #6b7280)",
                      fontSize: 12,
                    }}
                  />
                  <Tooltip formatter={(v) => secondsToMMSS(v)} />
                  <Area
                    type="monotone"
                    dataKey="seconds"
                    stroke="#22c55e"
                    fill="#22c55e"
                    fillOpacity={0.2}
                    strokeWidth={2}
                    name="Rezistenta"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserProgressPage;
